import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Alert,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  Checkbox,
  ListItemText,
  Chip,
  OutlinedInput,
  Paper,
  InputAdornment,
  FormControlLabel,
} from '@mui/material';
import {
  Close as CloseIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useFamily } from '../../context';
import { FamilySelector, useFamilySelection, FileAttachments } from '../common';
import { updateData } from '../../services/dataApi';

const ACTIVITY_TYPES = [
  { key: 'sports', label: 'Sports', color: '#10B981' },
  { key: 'music', label: 'Music', color: '#8B5CF6' },
  { key: 'art', label: 'Art', color: '#F59E0B' },
  { key: 'dance', label: 'Dance', color: '#EC4899' },
  { key: 'academic', label: 'Academic', color: '#3B82F6' },
  { key: 'other', label: 'Other', color: '#6B7280' },
];

const SCHEDULE_TYPES = [
  { key: 'practice', label: 'Practice' },
  { key: 'game', label: 'Game' },
  { key: 'performance', label: 'Performance' },
  { key: 'recital', label: 'Recital' },
  { key: 'lesson', label: 'Lesson' },
  { key: 'other', label: 'Other' },
];

const DAYS_OF_WEEK = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

interface ScheduleEntry {
  id: string;
  scheduleType: string;
  days: string[];
  startTime: string;
  endTime: string;
  location: string;
  isRecurring: boolean;
}

export interface ActivityData {
  name: string;
  type?: string;
  teamName?: string;
  coach?: string;
  cost?: number;
  location?: string;
  notes?: string;
  schedules?: ScheduleEntry[];
  schedule?: string;
  fileIds?: string[];
}

interface ActivityFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: ActivityData, personIds: string[], familyIds: string[]) => Promise<void>;
  onDelete?: () => Promise<void>;
  activity?: { id: string; person_id?: string; person_ids?: string[]; data?: ActivityData } | null;
}

const createEmptySchedule = (): ScheduleEntry => ({
  id: `sched_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
  scheduleType: 'practice',
  days: [],
  startTime: '',
  endTime: '',
  location: '',
  isRecurring: true,
});

export const ActivityFormDialog: React.FC<ActivityFormDialogProps> = ({
  open,
  onClose,
  onSave,
  onDelete,
  activity,
}) => {
  const { memberList, getMemberName, getMemberColor } = useFamily();
  const isEditing = !!activity;
  const {
    selectedFamilyIds,
    setSelectedFamilyIds,
    showSelector: showFamilySelector,
  } = useFamilySelection();

  const [name, setName] = useState('');
  const [type, setType] = useState('');
  const [teamName, setTeamName] = useState('');
  const [coach, setCoach] = useState('');
  const [cost, setCost] = useState('');
  const [notes, setNotes] = useState('');
  const [schedules, setSchedules] = useState<ScheduleEntry[]>([]);
  const [fileIds, setFileIds] = useState<string[]>([]);
  const [selectedPersonIds, setSelectedPersonIds] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      if (activity) {
        const data = activity.data;
        setName(data?.name || '');
        setType(data?.type || '');
        setTeamName(data?.teamName || '');
        setCoach(data?.coach || '');
        setCost(data?.cost?.toString() || '');
        setNotes(data?.notes || '');
        setSchedules(data?.schedules && data.schedules.length > 0 ? data.schedules : []);
        setFileIds(data?.fileIds || []);
        setSelectedPersonIds(activity.person_ids?.length ? activity.person_ids : activity.person_id ? [activity.person_id] : []);
      } else {
        setName('');
        setType('');
        setTeamName('');
        setCoach('');
        setCost('');
        setNotes('');
        setSchedules([]);
        setFileIds([]);
        setSelectedPersonIds(memberList.length > 0 ? [memberList[0].id] : []);
      }
      setError(null);
    }
  }, [open, activity, memberList]);

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Activity name is required');
      return;
    }
    if (selectedPersonIds.length === 0) {
      setError('Please select at least one person for this activity');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const costNum = cost ? parseFloat(cost) : undefined;
      await onSave(
        {
          name: name.trim(),
          type: type || undefined,
          teamName: teamName.trim() || undefined,
          coach: coach.trim() || undefined,
          cost: costNum && !isNaN(costNum) ? costNum : undefined,
          notes: notes.trim() || undefined,
          schedules: schedules.length > 0 ? schedules : undefined,
          fileIds: fileIds.length > 0 ? fileIds : undefined,
        },
        selectedPersonIds,
        selectedFamilyIds
      );
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save activity');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!onDelete) return;
    setSaving(true);
    try {
      await onDelete();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to delete activity');
    } finally {
      setSaving(false);
    }
  };

  const handlePersonChange = (event: any) => {
    const value = event.target.value;
    if (value.length > 0) {
      setSelectedPersonIds(typeof value === 'string' ? value.split(',') : value);
    }
  };

  const addSchedule = () => {
    setSchedules((prev) => [...prev, createEmptySchedule()]);
  };

  const removeSchedule = (id: string) => {
    setSchedules((prev) => prev.filter((s) => s.id !== id));
  };

  const updateSchedule = (id: string, field: keyof ScheduleEntry, value: any) => {
    setSchedules((prev) => prev.map((s) => s.id === id ? { ...s, [field]: value } : s));
  };

  const toggleScheduleDay = (schedId: string, day: string) => {
    setSchedules((prev) => prev.map((s) => {
      if (s.id !== schedId) return s;
      const days = s.days.includes(day) ? s.days.filter((d) => d !== day) : [...s.days, day];
      return { ...s, days };
    }));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">{isEditing ? 'Edit Activity' : 'New Activity'}</Typography>
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        <Grid container spacing={2}>
          {/* Activity Type */}
          <Grid size={12}>
            <Typography variant="subtitle2" gutterBottom>Type</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {ACTIVITY_TYPES.map((at) => (
                <Chip
                  key={at.key}
                  label={at.label}
                  onClick={() => setType(type === at.key ? '' : at.key)}
                  variant={type === at.key ? 'filled' : 'outlined'}
                  sx={{
                    cursor: 'pointer',
                    ...(type === at.key && { bgcolor: at.color, color: '#fff', '&:hover': { bgcolor: at.color } }),
                  }}
                />
              ))}
            </Box>
          </Grid>

          <Grid size={12}>
            <TextField label="Activity Name" value={name} onChange={(e) => setName(e.target.value)} fullWidth required autoFocus placeholder="e.g., Soccer, Piano Lessons, Dance Class" />
          </Grid>

          {showFamilySelector && !isEditing && (
            <Grid size={12}>
              <FamilySelector value={selectedFamilyIds} onChange={setSelectedFamilyIds} />
            </Grid>
          )}

          {/* Person selector */}
          <Grid size={12}>
            <FormControl fullWidth required>
              <InputLabel>For</InputLabel>
              <Select
                multiple
                value={selectedPersonIds}
                onChange={handlePersonChange}
                input={<OutlinedInput label="For" />}
                disabled={isEditing}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((personId) => {
                      const memberName = getMemberName(personId);
                      const color = getMemberColor(personId);
                      return (
                        <Chip key={personId} label={memberName} size="small" avatar={<Avatar sx={{ bgcolor: color, width: 20, height: 20, fontSize: 10 }}>{memberName.charAt(0)}</Avatar>} />
                      );
                    })}
                  </Box>
                )}
              >
                {memberList.map((member) => {
                  const memberName = getMemberName(member.id);
                  const color = getMemberColor(member.id);
                  return (
                    <MenuItem key={member.id} value={member.id}>
                      <Checkbox checked={selectedPersonIds.includes(member.id)} />
                      <Avatar sx={{ bgcolor: color, width: 24, height: 24, fontSize: 12, mr: 1 }}>{memberName.charAt(0)}</Avatar>
                      <ListItemText primary={memberName} />
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField label="Team Name" value={teamName} onChange={(e) => setTeamName(e.target.value)} fullWidth placeholder="e.g., Eagles, Studio A" />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField label="Coach/Instructor" value={coach} onChange={(e) => setCoach(e.target.value)} fullWidth />
          </Grid>

          <Grid size={12}>
            <TextField
              label="Cost"
              value={cost}
              onChange={(e) => setCost(e.target.value)}
              fullWidth
              type="number"
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              placeholder="Per season or monthly"
            />
          </Grid>

          {/* Schedules */}
          <Grid size={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle2">Schedules</Typography>
              <Button size="small" startIcon={<AddIcon />} onClick={addSchedule}>Add Schedule</Button>
            </Box>

            {schedules.map((sched) => (
              <Paper key={sched.id} variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {SCHEDULE_TYPES.map((st) => (
                      <Chip
                        key={st.key}
                        label={st.label}
                        size="small"
                        onClick={() => updateSchedule(sched.id, 'scheduleType', st.key)}
                        variant={sched.scheduleType === st.key ? 'filled' : 'outlined'}
                        color={sched.scheduleType === st.key ? 'primary' : 'default'}
                        sx={{ cursor: 'pointer' }}
                      />
                    ))}
                  </Box>
                  <IconButton size="small" onClick={() => removeSchedule(sched.id)}><CloseIcon fontSize="small" /></IconButton>
                </Box>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
                  {DAYS_OF_WEEK.map((day) => (
                    <Chip
                      key={day}
                      label={day}
                      size="small"
                      onClick={() => toggleScheduleDay(sched.id, day)}
                      variant={(sched.days || []).includes(day) ? 'filled' : 'outlined'}
                      color={(sched.days || []).includes(day) ? 'primary' : 'default'}
                      sx={{ cursor: 'pointer' }}
                    />
                  ))}
                </Box>

                <Grid container spacing={1}>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <TextField label="Start" type="time" value={sched.startTime} onChange={(e) => updateSchedule(sched.id, 'startTime', e.target.value)} fullWidth size="small" InputLabelProps={{ shrink: true }} />
                  </Grid>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <TextField label="End" type="time" value={sched.endTime} onChange={(e) => updateSchedule(sched.id, 'endTime', e.target.value)} fullWidth size="small" InputLabelProps={{ shrink: true }} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField label="Location" value={sched.location} onChange={(e) => updateSchedule(sched.id, 'location', e.target.value)} fullWidth size="small" />
                  </Grid>
                </Grid>

                <FormControlLabel
                  control={<Checkbox size="small" checked={sched.isRecurring} onChange={(e) => updateSchedule(sched.id, 'isRecurring', e.target.checked)} />}
                  label={<Typography variant="body2">Recurring</Typography>}
                  sx={{ mt: 0.5 }}
                />
              </Paper>
            ))}

            {schedules.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                No schedules added. Click "Add Schedule" to set up practice times, games, etc.
              </Typography>
            )}
          </Grid>

          {/* Attachments */}
          <Grid size={12}>
            <FileAttachments
              fileIds={fileIds}
              onChange={setFileIds}
              onPersist={activity ? async (ids) => {
                const data = activity.data || {};
                await updateData(activity.id, 'activity', { ...data, fileIds: ids });
              } : undefined}
              dataId={activity?.id}
              canUpload={isEditing}
            />
          </Grid>

          <Grid size={12}>
            <TextField label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} fullWidth multiline rows={2} />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        {isEditing && onDelete && (
          <Button onClick={handleDelete} color="error" startIcon={<DeleteIcon />} disabled={saving} sx={{ mr: 'auto' }}>Delete</Button>
        )}
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? 'Saving...' : isEditing ? 'Save' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ActivityFormDialog;
